<script setup>
import { onMounted, ref, defineProps } from "vue";
import { RiArrowDownSLine, RiArrowRightSLine } from "vue-remix-icons";
import { RouterLink } from "vue-router";
import simplebar from "simplebar-vue";
import { useAuth } from "@/composables/useAuth";
import { useUsers } from "@/composables/useUsers";

import { layout, toggleSidebarCollapse } from "@/stores/layout";

const isReportAccordionOpen = ref(false);
const isTransactionAccordionOpen = ref(false);
const isMediaAccordionOpen = ref(false);
const { getUserById } = useUsers();

const getUserId = () => localStorage.getItem("userId");

// Toggle function for each accordion
const toggleAccordion = (accordionType) => {
  if (accordionType === "report") {
    isReportAccordionOpen.value = !isReportAccordionOpen.value;
  } else if (accordionType === "transaction") {
    isTransactionAccordionOpen.value = !isTransactionAccordionOpen.value;
  } else if (accordionType === "media") {
    isMediaAccordionOpen.value = !isMediaAccordionOpen.value;
  }
};

const userId = getUserId();

const user = ref({});
const roleId = ref(null); // ref untuk role_id

onMounted(async () => {
  try {
    // Tunggu userId
    const userId = await getUserId();
    if (!userId) throw new Error("User ID not found");

    // Tunggu data user dari API
    const response = await getUserById(userId);
    user.value = response;

    console.log("USER DATA ===", user.value);

    // Tunggu sampai roles benar-benar ada
    if (user.value?.roles?.length > 0) {
      roleId.value = user.value.roles[0]?.UserRoles?.role_id || null;
      console.log("ROLE ID ===", roleId.value);
      localStorage.setItem(
        "role_id",
        user.value.roles[0]?.UserRoles?.role_id || null
      );
    } else {
      console.warn("User tidak memiliki roles atau field roles kosong");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      delete axios.defaults.headers.common["Authorization"];
      router.push("/login");
    }

    // Auto init UI
    setTimeout(() => {
      window.HSStaticMethods?.autoInit?.();
    }, 100);
  } catch (error) {
    console.error("Gagal mengambil detail user:", error);
  }
});

// console.log("ROLES USER ===", user);
</script>

<template>
  <div
    class="fixed inset-y-0 ltr:-start-[280px] ltr:xl:start-0 rtl:-right-[280px] rtl:xl:right-0 hs-overlay hs-overlay-open:start-0 bg-white dark:bg-gray-900 border-r rtl:border-e rtl:border-r-0 border-dashed dark:border-none w-sidebar z-sidebar transition-width duration-200 ease-in-out"
    :class="{ 'mini-sidebar': layout.sidebar === 'collapse' }"
  >
    <div
      class="flex items-center justify-between pt-[17px] pb-2 pl-8 pr-4 h-[70px]"
    >
      <RouterLink to="/dashboard">
        <img
          src="@/assets/images/dcrl_text.png"
          alt="Logo"
          style="width: 30%;"
        />
      </RouterLink>

      <!-- Mobile screen close button -->
      <button
        aria-label="Close"
        tabindex="0"
        type="button"
        class="inline-block p-2 text-gray-500 close-sidebar btn-dark-icon-hover size-10 xl:!hidden"
        @click="toggleSidebarCollapse"
      >
        <svg
          class="inline-block text-lg fill-current size-6"
          focusable="false"
          aria-hidden="true"
          viewBox="-2 0 19 16"
        >
          <path
            class="opacity-40"
            d="M1 1.75C0.447187 1.75 0 2.19719 0 2.75V12.75C0 13.3028 0.447187 13.75 1 13.75C1.55281 13.75 2 13.3028 2 12.75V2.75C2 2.19719 1.55281 1.75 1 1.75Z"
          ></path>
          <path
            d="M8.37634 3.20966C8.76697 2.81903 9.39978 2.81903 9.79041 3.20966C10.181 3.60028 10.181 4.23153 9.78978 4.62278L7.49666 6.91591H13.0842C13.637 6.91591 14.0842 7.36309 14.0842 7.91591C14.0842 8.46872 13.637 8.91591 13.0842 8.91591H7.49978L9.79119 11.209C9.9865 11.404 10.0842 11.6597 10.0842 11.9159C10.0842 12.1722 9.98728 12.4284 9.79041 12.6237C9.39978 13.0143 8.76697 13.0143 8.37634 12.6237L4.37634 8.62372C3.98572 8.23309 3.98572 7.60028 4.37634 7.20966L8.37634 3.20966Z"
          ></path>
        </svg>
      </button>
      <!-- Web screen close button -->
      <button
        name="mini-sidebar-button"
        @click="toggleSidebarCollapse"
        type="button"
        class="hidden p-2 text-gray-500 close-sidebar btn-dark-icon-hover size-10 xl:inline-block"
      >
        <svg
          class="inline-block text-lg fill-current size-6"
          focusable="false"
          aria-hidden="true"
          viewBox="-2 0 19 16"
        >
          <path
            class="opacity-40"
            d="M1 1.75C0.447187 1.75 0 2.19719 0 2.75V12.75C0 13.3028 0.447187 13.75 1 13.75C1.55281 13.75 2 13.3028 2 12.75V2.75C2 2.19719 1.55281 1.75 1 1.75Z"
          ></path>
          <path
            d="M8.37634 3.20966C8.76697 2.81903 9.39978 2.81903 9.79041 3.20966C10.181 3.60028 10.181 4.23153 9.78978 4.62278L7.49666 6.91591H13.0842C13.637 6.91591 14.0842 7.36309 14.0842 7.91591C14.0842 8.46872 13.637 8.91591 13.0842 8.91591H7.49978L9.79119 11.209C9.9865 11.404 10.0842 11.6597 10.0842 11.9159C10.0842 12.1722 9.98728 12.4284 9.79041 12.6237C9.39978 13.0143 8.76697 13.0143 8.37634 12.6237L4.37634 8.62372C3.98572 8.23309 3.98572 7.60028 4.37634 7.20966L8.37634 3.20966Z"
          ></path>
        </svg>
      </button>
    </div>
    <simplebar class="h-[calc(100%-68px)] overflow-y-auto overflow-x-hidden">
      <div class="px-4" id="navbar_links">
        <RouterLink
          to="/dashboard"
          class="menu-link hover:bg-opacity-[0.04] rounded-full"
          active-class="active-link"
          exact-active-class="active-link"
        >
          <svg focusable="false" aria-hidden="true" viewBox="0 0 14 12">
            <path
              d="M7 2.8V1.05C7 0.470094 7.47031 0 8.05 0H12.95C13.5297 0 14 0.470094 14 1.05V5.95C14 6.52969 13.5297 7 12.95 7H8.05C7.47031 7 7 6.52969 7 5.95V4.9H8.4V5.6H12.6V1.4H8.4V2.8H7Z"
            ></path>
            <path
              class="opacity-40"
              d="M5.24998 1.05C5.24998 1.62991 4.77967 2.1 4.19998 2.1C3.62029 2.1 3.14998 1.62991 3.14998 1.05C3.14998 0.470094 3.62029 0 4.19998 0C4.77967 0 5.24998 0.470094 5.24998 1.05ZM4.02498 7.7V10.5C4.02498 10.8872 3.71217 11.2 3.32498 11.2C2.93779 11.2 2.62498 10.8872 2.62498 10.5V5.61969L2.00001 6.66094C1.80095 6.99125 1.37089 7.09844 1.03948 6.89937C0.708074 6.70031 0.600887 6.27156 0.800168 5.93906L2.07482 3.81938C2.45436 3.18719 3.13686 2.8 3.87404 2.8H8.74998C9.13717 2.8 9.44998 3.11281 9.44998 3.5C9.44998 3.88719 9.13717 4.2 8.74998 4.2H5.77498V10.5C5.77498 10.8872 5.46217 11.2 5.07498 11.2C4.68779 11.2 4.37498 10.8872 4.37498 10.5V7.7H4.02498Z"
            ></path>
          </svg>
          <span>Dashboard</span>
        </RouterLink>

        <p class="mt-6 menu-title">Management</p>

        <template> </template>
        <div class="hs-accordion-group">
          <!-- users -->
          <template v-if="roleId === 1">
            <RouterLink
              to="/users"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg focusable="false" aria-hidden="true" viewBox="0 0 14 12">
                <path
                  d="M6.9978 7.20039C8.25364 7.20039 9.25311 6.18189 9.25311 4.92539C9.25311 3.66889 8.23505 2.65039 6.9978 2.65039C5.74196 2.65039 4.74249 3.66889 4.74249 4.92539C4.7228 6.18102 5.74218 7.20039 6.9978 7.20039ZM8.09155 7.90039H5.90843C4.19124 7.90039 2.79999 9.20633 2.79999 10.8163C2.79999 11.1401 3.0778 11.4004 3.42124 11.4004H10.5787C10.9222 11.4004 11.2 11.1401 11.2 10.8163C11.2 9.20633 9.80874 7.90039 8.09155 7.90039Z"
                ></path>
                <path
                  class="opacity-40"
                  d="M2.8 3.5C3.76644 3.5 4.55 2.71644 4.55 1.75C4.55 0.783562 3.76687 0 2.8 0C1.83356 0 1.05 0.783562 1.05 1.75C1.05 2.71644 1.83356 3.5 2.8 3.5ZM11.2 3.5C12.1664 3.5 12.95 2.71644 12.95 1.75C12.95 0.783562 12.1669 0 11.2 0C10.2336 0 9.45 0.783562 9.45 1.75C9.45 2.71644 10.2331 3.5 11.2 3.5ZM12.0728 4.2H10.7201C10.4401 4.2 10.1758 4.26643 9.93563 4.38025C9.94875 4.49531 9.97062 4.60687 9.97062 4.725C9.97062 5.46241 9.69106 6.12959 9.24525 6.65H13.6137C13.8272 6.65 14 6.46625 14 6.24094C14 5.11437 13.1381 4.2 12.0728 4.2ZM4.05781 4.37719C3.81937 4.24594 3.55687 4.2 3.27906 4.2H1.92675C0.86275 4.2 0 5.11437 0 6.24094C0 6.46625 0.172528 6.65 0.385438 6.65H4.7495C4.30369 6.12959 4.02413 5.46241 4.02413 4.725C4.02281 4.60687 4.04469 4.49313 4.05781 4.37719Z"
                ></path>
              </svg>
              <span>Users </span>
            </RouterLink>

            <!-- Outlet -->
            <RouterLink
              to="/outlets"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M21 13.2422V20H22V22H2V20H3V13.2422C1.79401 12.435 1 11.0602 1 9.5C1 8.67286 1.22443 7.87621 1.63322 7.19746L4.3453 2.5C4.52393 2.1906 4.85406 2 5.21132 2H18.7887C19.1459 2 19.4761 2.1906 19.6547 2.5L22.3575 7.18172C22.7756 7.87621 23 8.67286 23 9.5C23 11.0602 22.206 12.435 21 13.2422ZM19 13.9725C18.8358 13.9907 18.669 14 18.5 14C17.2409 14 16.0789 13.478 15.25 12.6132C14.4211 13.478 13.2591 14 12 14C10.7409 14 9.5789 13.478 8.75 12.6132C7.9211 13.478 6.75911 14 5.5 14C5.331 14 5.16417 13.9907 5 13.9725V20H19V13.9725ZM5.78865 4L3.35598 8.21321C3.12409 8.59843 3 9.0389 3 9.5C3 10.8807 4.11929 12 5.5 12C6.53096 12 7.44467 11.3703 7.82179 10.4295C8.1574 9.59223 9.3426 9.59223 9.67821 10.4295C10.0553 11.3703 10.969 12 12 12C13.031 12 13.9447 11.3703 14.3218 10.4295C14.6574 9.59223 15.8426 9.59223 16.1782 10.4295C16.5553 11.3703 17.469 12 18.5 12C19.8807 12 21 10.8807 21 9.5C21 9.0389 20.8759 8.59843 20.6347 8.19746L18.2113 4H5.78865Z"
                ></path>
              </svg>
              <span>Outlets</span>
            </RouterLink>

            <!-- Booth -->
            <RouterLink
              to="/booths"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M21 13V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V13H2V11L3 6H21L22 11V13H21ZM5 13V19H19V13H5ZM6 14H14V17H6V14ZM3 3H21V5H3V3Z"
                ></path>
              </svg>
              <span>Booths</span>
            </RouterLink>

            <!-- Plan -->
            <RouterLink
              to="/plans"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M15 21H9V10H15V21ZM17 21V10H22V20C22 20.5523 21.5523 21 21 21H17ZM7 21H3C2.44772 21 2 20.5523 2 20V10H7V21ZM22 8H2V4C2 3.44772 2.44772 3 3 3H21C21.5523 3 22 3.44772 22 4V8Z"
                ></path>
              </svg>
              <span>Plans</span>
            </RouterLink>

            <!-- Plan -->
            <RouterLink
              to="/addons"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M15 21H9V10H15V21ZM17 21V10H22V20C22 20.5523 21.5523 21 21 21H17ZM7 21H3C2.44772 21 2 20.5523 2 20V10H7V21ZM22 8H2V4C2 3.44772 2.44772 3 3 3H21C21.5523 3 22 3.44772 22 4V8Z"
                ></path>
              </svg>
              <span>Add-Ons</span>
            </RouterLink>

            <!-- Voucher -->
            <RouterLink
              to="/vouchers"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M11.0049 20.9997C11.0049 20.1712 10.3333 19.4997 9.50488 19.4997C8.67646 19.4997 8.00488 20.1712 8.00488 20.9997H3.00488C2.4526 20.9997 2.00488 20.5519 2.00488 19.9997V3.99966C2.00488 3.44738 2.4526 2.99966 3.00488 2.99966H8.00488C8.00488 3.82809 8.67646 4.49966 9.50488 4.49966C10.3333 4.49966 11.0049 3.82809 11.0049 2.99966H21.0049C21.5572 2.99966 22.0049 3.44738 22.0049 3.99966V9.49966C20.6242 9.49966 19.5049 10.619 19.5049 11.9997C19.5049 13.3804 20.6242 14.4997 22.0049 14.4997V19.9997C22.0049 20.5519 21.5572 20.9997 21.0049 20.9997H11.0049ZM9.50488 10.4997C10.3333 10.4997 11.0049 9.82809 11.0049 8.99966C11.0049 8.17124 10.3333 7.49966 9.50488 7.49966C8.67646 7.49966 8.00488 8.17124 8.00488 8.99966C8.00488 9.82809 8.67646 10.4997 9.50488 10.4997ZM9.50488 16.4997C10.3333 16.4997 11.0049 15.8281 11.0049 14.9997C11.0049 14.1712 10.3333 13.4997 9.50488 13.4997C8.67646 13.4997 8.00488 14.1712 8.00488 14.9997C8.00488 15.8281 8.67646 16.4997 9.50488 16.4997Z"
                ></path>
              </svg>
              <span>Vouchers</span>
            </RouterLink>

            <!-- Templates -->
            <RouterLink
              to="/templates"
              class="menu-link hover:bg-opacity-[0.04] rounded-full"
              active-class="active-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M6.2134 8.62811L5.96682 9.19379C5.78637 9.60792 5.21357 9.60792 5.03312 9.19379L4.78656 8.62811C4.34706 7.61947 3.55545 6.81641 2.56767 6.37708L1.80805 6.03922C1.39732 5.85653 1.39732 5.25881 1.80805 5.07612L2.5252 4.75714C3.53838 4.30651 4.34417 3.47373 4.77612 2.43083L5.02932 1.81953C5.20578 1.39349 5.79417 1.39349 5.97063 1.81953L6.22382 2.43083C6.65577 3.47373 7.46158 4.30651 8.4748 4.75714L9.19188 5.07612C9.60271 5.25881 9.60271 5.85653 9.19188 6.03922L8.43228 6.37708C7.44451 6.81641 6.65288 7.61947 6.2134 8.62811ZM11.2381 12.2698L16 21H23L15 6L11.2381 12.2698ZM14 21L8 10L2 21H14Z"
                ></path>
              </svg>
              <span>Templates</span>
            </RouterLink>
          </template>

          <div class="hs-accordion">
            <RouterLink
              to="#!"
              class="hs-accordion-toggle menu-link hover:bg-opacity-[0.04] rounded-full dark:hs-accordion-active:text-error-400 hs-accordion-active:text-error-400"
              @click="toggleAccordion('transaction')"
            >
              <div class="flex items-center w-full">
                <svg focusable="false" aria-hidden="true" viewBox="0 0 14 18">
                  <path
                    d="M2.91669 5.83333C2.91669 5.5125 3.17773 5.25 3.50002 5.25H10.5C10.8209 5.25 11.0834 5.5125 11.0834 5.83333C11.0834 6.15417 10.8209 6.41667 10.5 6.41667H3.50002C3.17773 6.41667 2.91669 6.15417 2.91669 5.83333ZM2.91669 12.8333C2.91669 12.5125 3.17773 12.25 3.50002 12.25H10.5C10.8209 12.25 11.0834 12.5125 11.0834 12.8333C11.0834 13.1542 10.8209 13.4167 10.5 13.4167H3.50002C3.17773 13.4167 2.91669 13.1542 2.91669 12.8333ZM10.5 8.75C10.8209 8.75 11.0834 9.0125 11.0834 9.33333C11.0834 9.65417 10.8209 9.91667 10.5 9.91667H3.50002C3.17773 9.91667 2.91669 9.65417 2.91669 9.33333C2.91669 9.0125 3.17773 8.75 3.50002 8.75H10.5Z"
                  ></path>
                  <path
                    class="opacity-40"
                    d="M0.509323 0.0800625C0.819948 -0.0627083 1.1849 -0.0117177 1.44448 0.210656L2.91667 1.47255L4.38958 0.210656C4.71771 -0.0702188 5.19896 -0.0702188 5.52708 0.210656L7 1.47255L8.47292 0.210656C8.80104 -0.0702188 9.28229 -0.0702188 9.61042 0.210656L11.0833 1.47255L12.5562 0.210656C12.8151 -0.0117177 13.1797 -0.0627083 13.4896 0.0800625C13.7995 0.22287 14 0.533385 14 0.875V17.7917C14 18.1344 13.7995 18.4443 13.4896 18.5865C13.1797 18.7286 12.8151 18.6776 12.5562 18.4552L11.0833 17.1937L9.61042 18.4552C9.28229 18.7359 8.80104 18.7359 8.47292 18.4552L7 17.1937L5.52708 18.4552C5.19896 18.7359 4.71771 18.7359 4.38958 18.4552L2.91667 17.1937L1.44448 18.4552C1.1849 18.6776 0.819948 18.7286 0.509323 18.5865C0.198917 18.4443 0 18.1344 0 17.7917V0.875C0 0.533385 0.198917 0.222833 0.509323 0.0800625ZM3.5 5.25C3.17771 5.25 2.91667 5.5125 2.91667 5.83333C2.91667 6.15417 3.17771 6.41667 3.5 6.41667H10.5C10.8208 6.41667 11.0833 6.15417 11.0833 5.83333C11.0833 5.5125 10.8208 5.25 10.5 5.25H3.5ZM3.5 13.4167H10.5C10.8208 13.4167 11.0833 13.1542 11.0833 12.8333C11.0833 12.5125 10.8208 12.25 10.5 12.25H3.5C3.17771 12.25 2.91667 12.5125 2.91667 12.8333C2.91667 13.1542 3.17771 13.4167 3.5 13.4167ZM3.5 8.75C3.17771 8.75 2.91667 9.0125 2.91667 9.33333C2.91667 9.65417 3.17771 9.91667 3.5 9.91667H10.5C10.8208 9.91667 11.0833 9.65417 11.0833 9.33333C11.0833 9.0125 10.8208 8.75 10.5 8.75H3.5Z"
                  ></path>
                </svg>
                <span>Transactions</span>
              </div>
              <!-- Dynamic icon -->
              <RiArrowDownSLine
                v-if="isTransactionAccordionOpen"
                class="hidden text-lg hs-accordion-active:block"
              />
              <RiArrowRightSLine
                v-else
                class="block text-lg hs-accordion-active:hidden"
              />
            </RouterLink>
            <div
              v-if="isTransactionAccordionOpen"
              class="hs-accordion-content w-full overflow-hidden transition-[height] duration-300"
            >
              <RouterLink
                to="/create-transaction"
                class="menu-link hover:bg-opacity-[0.04] rounded-full"
                active-class="active-link"
              >
                <div class="mx-2 bg-gray-500 rounded-full size-1"></div>
                <span>Create Transaction</span>
              </RouterLink>
              <RouterLink
                to="/transactions"
                class="menu-link hover:bg-opacity-[0.04] rounded-full"
                active-class="active-link"
              >
                <div class="mx-2 bg-gray-500 rounded-full size-1"></div>
                <span>Transactions</span>
              </RouterLink>
            </div>
          </div>

          <!-- Photo -->

          <div class="hs-accordion">
            <RouterLink
              to="#!"
              class="hs-accordion-toggle menu-link hover:bg-opacity-[0.04] rounded-full dark:hs-accordion-active:text-error-400 hs-accordion-active:text-error-400"
              @click="toggleAccordion('media')"
            >
              <div class="flex items-center w-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M19.5762 14.5759L15.7073 10.707C15.3168 10.3164 14.6836 10.3164 14.2931 10.707L6.86516 18.1349C5.11372 16.6674 4 14.4637 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 12.9012 19.851 13.7676 19.5762 14.5759ZM12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM11 10C11 11.1046 10.1046 12 9 12C7.89543 12 7 11.1046 7 10C7 8.89543 7.89543 8 9 8C10.1046 8 11 8.89543 11 10Z"
                  ></path>
                </svg>
                <span>Photo Studio</span>
              </div>
              <!-- Dynamic icon -->
              <RiArrowDownSLine
                v-if="isMediaAccordionOpen"
                class="hidden text-lg hs-accordion-active:block"
              />
              <RiArrowRightSLine
                v-else
                class="block text-lg hs-accordion-active:hidden"
              />
            </RouterLink>
            <div
              v-if="isMediaAccordionOpen"
              class="hs-accordion-content w-full overflow-hidden transition-[height] duration-300"
            >
              <RouterLink
                to="/dirs"
                class="menu-link hover:bg-opacity-[0.04] rounded-full"
                active-class="active-link"
              >
                <div class="mx-2 bg-gray-500 rounded-full size-1"></div>
                <span>File Foto</span>
              </RouterLink>
            </div>
          </div>

          <!-- Customer -->
          <RouterLink
            to="/customers"
            class="menu-link hover:bg-opacity-[0.04] rounded-full"
            active-class="active-link"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M17.841 15.659L18.017 15.836L18.1945 15.659C19.0732 14.7803 20.4978 14.7803 21.3765 15.659C22.2552 16.5377 22.2552 17.9623 21.3765 18.841L18.0178 22.1997L14.659 18.841C13.7803 17.9623 13.7803 16.5377 14.659 15.659C15.5377 14.7803 16.9623 14.7803 17.841 15.659ZM12 14V22H4C4 17.6651 7.44784 14.1355 11.7508 14.0038L12 14ZM12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1Z"
              ></path>
            </svg>
            <span>Customers</span>
          </RouterLink>

          <div class="hs-accordion">
            <RouterLink
              to="#!"
              class="hs-accordion-toggle menu-link hover:bg-opacity-[0.04] rounded-full dark:hs-accordion-active:text-error-400 hs-accordion-active:text-error-400"
              @click="toggleAccordion('report')"
            >
              <div class="flex items-center w-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M5 3V19H21V21H3V3H5ZM19.9393 5.93934L22.0607 8.06066L16 14.1213L13 11.121L9.06066 15.0607L6.93934 12.9393L13 6.87868L16 9.879L19.9393 5.93934Z"
                  ></path>
                </svg>
                <span>Reports</span>
              </div>
              <!-- Dynamic icon -->
              <RiArrowDownSLine
                v-if="isReportAccordionOpen"
                class="hidden text-lg hs-accordion-active:block"
              />
              <RiArrowRightSLine
                v-else
                class="block text-lg hs-accordion-active:hidden"
              />
            </RouterLink>
            <div
              v-if="isReportAccordionOpen"
              class="hs-accordion-content w-full overflow-hidden transition-[height] duration-300"
            >
              <RouterLink
                to="/report-packages"
                class="menu-link hover:bg-opacity-[0.04] rounded-full"
                active-class="active-link"
              >
                <div class="mx-2 bg-gray-500 rounded-full size-1"></div>
                <span>Paket</span>
              </RouterLink>
              <RouterLink
                to="/report-revenues"
                class="menu-link hover:bg-opacity-[0.04] rounded-full"
                active-class="active-link"
              >
                <div class="mx-2 bg-gray-500 rounded-full size-1"></div>
                <span>Revenues (Pendapatan)</span>
              </RouterLink>
              <RouterLink
                to="/report-revenues-by-method"
                class="menu-link hover:bg-opacity-[0.04] rounded-full"
                active-class="active-link"
              >
                <div class="mx-2 bg-gray-500 rounded-full size-1"></div>
                <span>Revenues By Method</span>
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </simplebar>
  </div>
</template>

<style>
.active-link {
    --tw-bg-opacity: 1
    background-color: rgb(254 241 244 / var(--tw-bg-opacity));
    --tw-text-opacity: 1;
    color: rgb(243 119 149/ var(--tw-text-opacity));
}

.active-link:hover {
    background-color: transparent;
}
</style>
